/**
 * Certificate Monitor (SysAdmin)
 * Lista och bevaka certifikat som går ut (OIDC private_key_jwt + manuella), med
 * statusfärger, varningströskel och förnyelse-instruktion.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  ShieldCheckIcon,
  ArrowPathIcon,
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { PageBackButton } from '@/components/common/PageBackButton';
import toast from 'react-hot-toast';
import DateInput from '@/components/common/DateInput';
import {
  certificateMonitorService,
  CertificateItem,
  CertStatus,
  ManualCertInput,
} from '@/services/sysadmin/certificateMonitorService';

const formatDate = (value: string | null): string => {
  if (!value) return '–';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '–';
  return new Intl.DateTimeFormat('sv-SE', { dateStyle: 'medium' }).format(d);
};

const statusClasses: Record<CertStatus, string> = {
  ok: 'bg-green-100 text-green-800 border-green-300',
  expiring: 'bg-amber-100 text-amber-800 border-amber-300',
  expired: 'bg-red-100 text-red-800 border-red-300',
  unknown: 'bg-gray-100 text-gray-700 border-gray-300',
};

const emptyForm: ManualCertInput = {
  name: '',
  certificate_pem: '',
  organization_name: '',
  valid_to: '',
  check_host: '',
  warning_days: 60,
  renewal_instructions: '',
  contact_email: '',
  notes: '',
  is_active: true,
};

const CertificateMonitor: React.FC = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<CertificateItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [editSource, setEditSource] = useState<'auto' | 'manual'>('manual');
  const [form, setForm] = useState<ManualCertInput>(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setIsLoading(true);
    try {
      const data = await certificateMonitorService.list();
      setItems(data);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || t('sysAdmin.certMonitor.loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const res = await certificateMonitorService.refresh();
      toast.success(
        t('sysAdmin.certMonitor.refreshedDetail', {
          oidc: res?.oidcUpdated ?? 0,
          host: res?.hostUpdated ?? 0,
          errors: res?.errors ?? 0,
        })
      );
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || t('sysAdmin.certMonitor.refreshError'));
    } finally {
      setIsRefreshing(false);
    }
  };

  const summary = useMemo(() => {
    const active = items.filter((c) => c.is_active);
    return {
      expired: active.filter((c) => c.status === 'expired').length,
      expiring: active.filter((c) => c.status === 'expiring').length,
    };
  }, [items]);

  const openCreate = () => {
    setEditId(null);
    setEditSource('manual');
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (item: CertificateItem) => {
    setEditId(item.id);
    setEditSource(item.source);
    setForm({
      name: item.name,
      certificate_pem: '',
      organization_name: item.organization_name || '',
      valid_to: item.valid_to ? item.valid_to.slice(0, 10) : '',
      check_host: item.check_host || '',
      warning_days: item.warning_days,
      renewal_instructions: item.renewal_instructions || '',
      contact_email: item.contact_email || '',
      notes: item.notes || '',
      is_active: !!item.is_active,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error(t('sysAdmin.certMonitor.nameRequired'));
      return;
    }
    setSaving(true);
    try {
      if (editId == null) {
        await certificateMonitorService.create(form);
        toast.success(t('sysAdmin.certMonitor.created'));
      } else {
        await certificateMonitorService.update(editId, form);
        toast.success(t('sysAdmin.certMonitor.updated'));
      }
      setShowModal(false);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || t('sysAdmin.certMonitor.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: CertificateItem) => {
    if (!window.confirm(t('sysAdmin.certMonitor.confirmDelete', { name: item.name }))) return;
    try {
      await certificateMonitorService.remove(item.id);
      toast.success(t('sysAdmin.certMonitor.deleted'));
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || t('sysAdmin.certMonitor.deleteError'));
    }
  };

  const statusLabel = (item: CertificateItem): string => {
    if (item.status === 'expired') return t('sysAdmin.certMonitor.statusExpired');
    if (item.status === 'expiring')
      return t('sysAdmin.certMonitor.statusExpiring', { days: item.days_left ?? 0 });
    if (item.status === 'ok')
      return t('sysAdmin.certMonitor.statusOk', { days: item.days_left ?? 0 });
    return t('sysAdmin.certMonitor.statusUnknown');
  };

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full overflow-auto">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <PageBackButton />
          <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
            <ShieldCheckIcon className="w-6 h-6 text-primary" />
            {t('sysAdmin.certMonitor.title')}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-border hover:bg-bgElevated disabled:opacity-50"
          >
            <ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {t('sysAdmin.certMonitor.refresh')}
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-bgPrimary text-white hover:opacity-90"
          >
            <PlusIcon className="w-4 h-4" />
            {t('sysAdmin.certMonitor.addManual')}
          </button>
        </div>
      </div>

      <p className="text-sm text-textSecondary mb-4">{t('sysAdmin.certMonitor.description')}</p>

      {(summary.expired > 0 || summary.expiring > 0) && (
        <div className="mb-4 flex items-start gap-2 bg-amber-50 border-l-4 border-amber-400 p-3 rounded">
          <ExclamationTriangleIcon className="w-5 h-5 text-amber-500 mt-0.5" />
          <p className="text-sm text-amber-800">
            {t('sysAdmin.certMonitor.bannerSummary', {
              expired: summary.expired,
              expiring: summary.expiring,
            })}
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="text-textSecondary p-6">{t('common.loading')}</div>
      ) : items.length === 0 ? (
        <div className="text-textSecondary p-6">{t('sysAdmin.certMonitor.empty')}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-textSecondary border-b border-border">
                <th className="py-2 pr-4">{t('sysAdmin.certMonitor.colName')}</th>
                <th className="py-2 pr-4">{t('sysAdmin.certMonitor.colStatus')}</th>
                <th className="py-2 pr-4">{t('sysAdmin.certMonitor.colExpires')}</th>
                <th className="py-2 pr-4">{t('sysAdmin.certMonitor.colSource')}</th>
                <th className="py-2 pr-4">{t('sysAdmin.certMonitor.colRenewal')}</th>
                <th className="py-2 pr-4 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  className={`border-b border-border align-top ${item.is_active ? '' : 'opacity-50'}`}
                >
                  <td className="py-3 pr-4">
                    <div className="font-medium text-textDefault">{item.name}</div>
                    {item.organization_name && (
                      <div className="text-xs text-textSecondary">{item.organization_name}</div>
                    )}
                    {item.last_error && (
                      <div className="text-xs text-red-600 mt-1">⚠ {item.last_error}</div>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    <span className={`inline-block px-2 py-1 rounded border text-xs whitespace-nowrap ${statusClasses[item.status]}`}>
                      {statusLabel(item)}
                    </span>
                  </td>
                  <td className="py-3 pr-4 whitespace-nowrap">{formatDate(item.valid_to)}</td>
                  <td className="py-3 pr-4">
                    <span className="text-xs px-2 py-0.5 rounded bg-bgElevated border border-border">
                      {item.source === 'auto'
                        ? t('sysAdmin.certMonitor.sourceAuto')
                        : t('sysAdmin.certMonitor.sourceManual')}
                    </span>
                  </td>
                  <td className="py-3 pr-4 max-w-md">
                    <div className="text-xs text-textSecondary whitespace-pre-wrap">
                      {item.renewal_instructions || '–'}
                    </div>
                    {item.contact_email && (
                      <div className="text-xs text-bgPrimary mt-1">{item.contact_email}</div>
                    )}
                  </td>
                  <td className="py-3 pr-2">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(item)}
                        className="p-1.5 rounded hover:bg-bgElevated"
                        title={t('common.edit')}
                      >
                        <PencilSquareIcon className="w-4 h-4 text-textDefault" />
                      </button>
                      {item.source === 'manual' && (
                        <button
                          onClick={() => handleDelete(item)}
                          className="p-1.5 rounded hover:bg-red-50"
                          title={t('common.delete')}
                        >
                          <TrashIcon className="w-4 h-4 text-red-500" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-bgContainer rounded-lg w-full max-w-lg max-h-[90vh] overflow-auto p-5">
            <h2 className="text-lg font-semibold text-textDefault mb-4">
              {editId == null
                ? t('sysAdmin.certMonitor.addManual')
                : t('sysAdmin.certMonitor.editTitle')}
            </h2>

            <div className="space-y-3">
              <div>
                <label className="block text-sm text-textSecondary mb-1">
                  {t('sysAdmin.certMonitor.fieldName')} *
                </label>
                <input
                  type="text"
                  value={form.name}
                  disabled={editSource === 'auto'}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-border bg-bgElevated text-textDefault disabled:opacity-60"
                />
              </div>

              {editSource === 'manual' && (
                <>
                  <div>
                    <label className="block text-sm text-textSecondary mb-1">
                      {t('sysAdmin.certMonitor.fieldOrg')}
                    </label>
                    <input
                      type="text"
                      value={form.organization_name || ''}
                      onChange={(e) => setForm({ ...form, organization_name: e.target.value })}
                      className="w-full px-3 py-2 rounded border border-border bg-bgElevated text-textDefault"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-textSecondary mb-1">
                      {t('sysAdmin.certMonitor.fieldCheckHost')}
                    </label>
                    <input
                      type="text"
                      value={form.check_host || ''}
                      placeholder="dqfleet.deque.se"
                      onChange={(e) => setForm({ ...form, check_host: e.target.value })}
                      className="w-full px-3 py-2 rounded border border-border bg-bgElevated text-textDefault"
                    />
                    <p className="text-xs text-textSecondary mt-1">{t('sysAdmin.certMonitor.fieldCheckHostHelp')}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-textSecondary mb-1">
                      {t('sysAdmin.certMonitor.fieldPem')}
                    </label>
                    <textarea
                      rows={4}
                      value={form.certificate_pem || ''}
                      placeholder={'-----BEGIN CERTIFICATE-----'}
                      onChange={(e) => setForm({ ...form, certificate_pem: e.target.value })}
                      className="w-full px-3 py-2 rounded border border-border bg-bgElevated text-textDefault font-mono text-xs"
                    />
                    <p className="text-xs text-textSecondary mt-1">{t('sysAdmin.certMonitor.fieldPemHelp')}</p>
                  </div>
                  <div>
                    <label className="block text-sm text-textSecondary mb-1">
                      {t('sysAdmin.certMonitor.fieldValidTo')}
                    </label>
                    <DateInput value={form.valid_to || ''}
                      onChange={(e) => setForm({ ...form, valid_to: e.target.value })}
                      className="w-full px-3 py-2 rounded border border-border bg-bgElevated text-textDefault"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm text-textSecondary mb-1">
                  {t('sysAdmin.certMonitor.fieldWarningDays')}
                </label>
                <input
                  type="number"
                  min={1}
                  value={form.warning_days ?? 60}
                  onChange={(e) => setForm({ ...form, warning_days: Number(e.target.value) })}
                  className="w-full px-3 py-2 rounded border border-border bg-bgElevated text-textDefault"
                />
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">
                  {t('sysAdmin.certMonitor.fieldRenewal')}
                </label>
                <textarea
                  rows={3}
                  value={form.renewal_instructions || ''}
                  onChange={(e) => setForm({ ...form, renewal_instructions: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-border bg-bgElevated text-textDefault"
                />
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">
                  {t('sysAdmin.certMonitor.fieldContact')}
                </label>
                <input
                  type="email"
                  value={form.contact_email || ''}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-border bg-bgElevated text-textDefault"
                />
              </div>
              <div>
                <label className="block text-sm text-textSecondary mb-1">
                  {t('sysAdmin.certMonitor.fieldNotes')}
                </label>
                <textarea
                  rows={2}
                  value={form.notes || ''}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2 rounded border border-border bg-bgElevated text-textDefault"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-textDefault">
                <input
                  type="checkbox"
                  checked={form.is_active !== false}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                {t('sysAdmin.certMonitor.fieldActive')}
              </label>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-bgElevated"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 text-sm rounded-lg bg-bgPrimary text-white hover:opacity-90 disabled:opacity-50"
              >
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateMonitor;
